import { useState, useCallback } from "react";

export interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

export interface Change {
  id: string;
  section: string;
  type: "add" | "edit" | "delete";
  description: string;
  fieldChanges?: FieldChange[];
}

interface UseChangesTrackerReturn {
  changes: Change[];
  addChange: (change: Change) => void;
  clearChange: (id: string) => void;
  clearAllChanges: () => void;
  hasChanges: boolean;
  /** Increments after a successful save; editors watch it to rebaseline. */
  savedSignal: number;
  /** Clears all changes and bumps savedSignal. */
  markSaved: () => void;
}

export function useChangesTracker(): UseChangesTrackerReturn {
  const [changes, setChanges] = useState<Change[]>([]);
  const [savedSignal, setSavedSignal] = useState(0);

  const markSaved = useCallback(() => {
    setChanges([]);
    setSavedSignal((n) => n + 1);
  }, []);

  const addChange = useCallback((change: Change) => {
    setChanges((prev) => {
      // Remove existing change with same id if it exists
      const filtered = prev.filter((c) => c.id !== change.id);
      return [...filtered, change];
    });
  }, []);

  const clearChange = useCallback((id: string) => {
    setChanges((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const clearAllChanges = useCallback(() => {
    setChanges([]);
  }, []);

  return {
    changes,
    addChange,
    clearChange,
    clearAllChanges,
    hasChanges: changes.length > 0,
    savedSignal,
    markSaved,
  };
}
